import React, { useContext, useEffect, useRef, useState } from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { encode, GlobalId } from '../../../id';
import { AuthContext } from 'api/context';

const PortalRedirect = (props) => {
  const {
    proposalId,
    leadId,
    loading,
    handleSaveProposal = null,
    canOpenPortal = true,
    onBlockedOpen = null,
    onSaveError = null,
  } = props;
  const { company, auth } = useContext(AuthContext);
  const session = auth.getToken();
  const proposalKey = `${company}:${leadId}:${proposalId}`;
  const linksSharing = process.env.REACT_APP_LINKS_SHARING_ENABLED === 'true';
  const [sharedHomeownerLink, setSharedHomeownerLink] = useState(null);
  const [sharingError, setSharingError] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [preparedFor, setPreparedFor] = useState(handleSaveProposal || linksSharing ? null : proposalKey);
  const preparationRequest = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setPreparedFor(handleSaveProposal || linksSharing ? null : proposalKey);
    setPreparing(false);
    setSharedHomeownerLink(null);
    setSharingError(null);
    preparationRequest.current = null;
  }, [handleSaveProposal, proposalKey, linksSharing, session]);

  const globalCompanyId = encode(new GlobalId('Organization', 'legacy', company));
  const globalProposalId = encode(new GlobalId('Proposal', 'legacy', proposalId));
  const globalCustomerId = encode(new GlobalId('Customer', 'legacy', leadId));
  const homeownerLink = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/apps/customer-portal/${globalCustomerId}?proposalId=${globalProposalId}&organizationId=${globalCompanyId}&viewAs=customer`;
  const consultantRedirect = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/apps/customer-portal/${globalCustomerId}?proposalId=${globalProposalId}&organizationId=${globalCompanyId}`;
  const consultantLink = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/auth/signin/legacy?session=${session}&organization=${globalCompanyId}&redirectTo=${encodeURIComponent(
    consultantRedirect
  )}`;

  const preparePortal = () => {
    if (!handleSaveProposal && !linksSharing) {
      setPreparedFor(proposalKey);
      return Promise.resolve(true);
    }

    if (preparationRequest.current) return preparationRequest.current;

    setPreparing(true);
    setPreparedFor(null);
    setSharingError(null);

    const request = Promise.resolve()
      .then(() => handleSaveProposal?.())
      .then(async () => {
        if (!linksSharing) return;
        const response = await fetch(`${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/apps/customer-portal/api/share-link`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', authorization: `Token ${session}` },
          body: JSON.stringify({ kind: 'lead', customerId: globalCustomerId, proposalId: globalProposalId }),
        });
        if (!response.ok) throw new Error('The shared homeowner link could not be confirmed. Please try again.');
        const result = await response.json();
        if (typeof result.url !== 'string') throw new Error('The shared homeowner link is unavailable.');
        if (mounted.current && preparationRequest.current === request) setSharedHomeownerLink(result.url);
      })
      .then(() => {
        if (mounted.current && preparationRequest.current === request) {
          setPreparedFor(proposalKey);
        }
        return true;
      })
      .catch((error) => {
        if (mounted.current && preparationRequest.current === request) {
          setPreparedFor(null);
          setSharingError('The homeowner link could not be prepared. Please try again.');
          if (onSaveError) onSaveError(error);
        }
        return false;
      })
      .finally(() => {
        if (preparationRequest.current === request) {
          preparationRequest.current = null;
          if (mounted.current) setPreparing(false);
        }
      });

    preparationRequest.current = request;
    return request;
  };

  const handleToggle = (show) => {
    if (!show || loading || preparing) return;

    if (!canOpenPortal) {
      if (onBlockedOpen) onBlockedOpen();
      return;
    }

    preparePortal();
  };

  const handleUnavailableLink = (event) => {
    event.preventDefault();
    if (!canOpenPortal && onBlockedOpen) onBlockedOpen();
  };

  const linksReady = canOpenPortal && !loading && !preparing && preparedFor === proposalKey;
  const linkProps = linksReady ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  const accessibilityList = [
    'WOLFRI',
    'AERIAL',
    'AERIA1',
    'SUNCHE',
    'JSCONS',
    'SUNLY2',
    'TEMPOS',
    'SIGESO',
    'BARDIA',
    'PRODUC',
  ];

  if (!linksSharing && !accessibilityList.includes(company) && !company.includes('WOLFR')) {
    return <></>;
  }

  return (
    <DropdownButton
      style={{ display: 'inline', ...(props?.style || {}) }}
      size={props?.size}
      className={`my-0 py-0 ${props?.size === 'sm' ? '.dropdown-menu-sm' : '.dropdown-menu-default'}`}
      variant="secondary"
      id="customer-portal-dropdown"
      title={preparing ? 'Preparing...' : 'Customer Portal'}
      disabled={loading || preparing}
      onToggle={handleToggle}
    >
      <Dropdown.Item
        className="px-1 my-0"
        disabled={!linksReady}
        href={linksReady ? (linksSharing ? sharedHomeownerLink : homeownerLink) : undefined}
        onClick={linksReady ? undefined : handleUnavailableLink}
        {...linkProps}
      >
        <small>Homeowner</small>
      </Dropdown.Item>
      {sharingError && <Dropdown.ItemText role="alert">{sharingError}</Dropdown.ItemText>}
      <Dropdown.Divider />
      <Dropdown.Item
        className="px-1 my-0"
        disabled={!linksReady}
        href={linksReady ? consultantLink : undefined}
        onClick={linksReady ? undefined : handleUnavailableLink}
        {...linkProps}
      >
        <small>Consultant</small>
      </Dropdown.Item>
    </DropdownButton>
  );
};

PortalRedirect.propTypes = {
  proposalId: PropTypes.string.isRequired,
  leadId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  handleSaveProposal: PropTypes.func,
  canOpenPortal: PropTypes.bool,
  onBlockedOpen: PropTypes.func,
  onSaveError: PropTypes.func,
  style: PropTypes.object,
};

export default PortalRedirect;
